import axiosInstance from "../api/axiosInstance";

/**
 * WebSocket URL helper.
 *
 * Every real-time feature used to hardcode a localhost websocket address, so
 * notifications, chat and agent presence all died the moment the app ran
 * anywhere but a developer's machine. Two failures, not one:
 *
 *   * the host is wrong outside local development, and
 *   * an insecure ws:// socket is blocked by the browser on an HTTPS page — it
 *     has to be wss://.
 *
 * Derive both from the same base the REST client already uses, so there is a
 * single place to configure the backend.
 */

/** Scheme + host for the API, e.g. "wss://api.example.com". */
export function getWsBaseUrl() {
  const httpBase =
    process.env.REACT_APP_WS_BASE_URL ||
    axiosInstance?.defaults?.baseURL ||
    process.env.REACT_APP_API_BASE_URL ||
    // Last resort: same origin as the page, which at least keeps the scheme right.
    (typeof window !== "undefined" ? window.location.origin : "http://localhost:8000");

  return httpBase
    .replace(/^https:\/\//, "wss://")
    .replace(/^http:\/\//, "ws://")
    .replace(/\/+$/, "");
}

/**
 * Build a full socket URL.
 *   buildWsUrl(`/ws/chat/${roomId}`, { access_token: token })
 */
export function buildWsUrl(path = "", params) {
  const base = getWsBaseUrl();
  const suffix = path.startsWith("/") ? path : `/${path}`;
  const url = `${base}${suffix}`;
  if (!params) return url;

  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  ).toString();
  return qs ? `${url}${url.includes("?") ? "&" : "?"}${qs}` : url;
}

/** Read the access token's claims, or null when there is no usable token. */
function tokenClaims() {
  try {
    const raw =
      (typeof localStorage !== "undefined" &&
        (localStorage.getItem("access_token") ||
          localStorage.getItem("token") ||
          localStorage.getItem("AuthToken"))) ||
      "";
    const part = raw.split(".")[1];
    if (!part) return null;
    return JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

/**
 * The channel id that identifies *this* user to the notification sockets.
 *
 * The server registers and pushes by the role-scoped `actual_id` (PA-/AG-/EM-),
 * falling back to `id` (US-...). It is NOT the token's `sub`, which is the
 * user's email address.
 */
export function getMyChannelId() {
  const claims = tokenClaims();
  if (!claims) return "";
  return String(claims.actual_id || claims.id || "");
}

/** Ids the server will accept as "your own" channel. */
function ownChannelIds() {
  const claims = tokenClaims();
  if (!claims) return [];
  return [claims.actual_id, claims.id].filter(Boolean).map(String);
}

/**
 * URL for a per-user notification socket, with the access token attached.
 *
 * The server used to register whatever subscriber id appeared in the path, with
 * no authentication at all — so knowing an id (they show up in ordinary API
 * responses) was enough to receive that user's ticket notifications and
 * dashboard alerts, across customers. It now requires the token and checks the
 * channel belongs to the caller, which means every call site must send it.
 * Forgetting is easy when the URL is assembled by hand, so build it here.
 *
 * `channel` is the path after /ws — usually the user's actual_id, or
 * `dashboard_alerts/<id>` / `dashboard_carousel_data/<id>` for those feeds.
 */
export function getWsUrl(channel) {
  const token =
    (typeof localStorage !== "undefined" &&
      (localStorage.getItem("access_token") || localStorage.getItem("token"))) ||
    "";
  const path = String(channel ?? "").replace(/^\/+/, "");

  // Most call sites derive this id themselves, and nine of them read the
  // token's `sub` first — which is the user's *email*, not an id the server
  // will accept. The socket then opens on /ws/<email>, the channel-ownership
  // check refuses it (close 1008) and every real-time feed on that page stays
  // silent. Repair the id here, at the single choke point, rather than in
  // every caller: the only per-user channel anyone may subscribe to is their
  // own, so it can always be recovered from the token.
  // Chat sockets are keyed by room id, not by a user id — never rewrite those.
  const owned = ownChannelIds();
  const mine = path.startsWith("chat/") ? "" : getMyChannelId();
  const segments = path.split("/");
  const idIndex = segments.length - 1; // "<id>" or "dashboard_alerts/<id>"
  const requested = segments[idIndex] || "";
  if (mine && requested && !owned.includes(requested)) {
    segments[idIndex] = mine;
  }
  const safePath = segments.join("/");

  return buildWsUrl(`/ws/${safePath}`, { access_token: token });
}

export default getWsBaseUrl;
